import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { MdOutlineWorkOutline } from "react-icons/md";
import axios from "../../../Utils/axiosInstance";
import CareerDetailModal from "./CareerDetailModal";
import CareerApplyModal from "../../UserSideComponents/Home/Components/CareerApplyModal";

const JobTypeBadge = ({ type }) => {
  const colors = {
    "Full-time": "bg-blue-50 text-blue-600",
    "Part-time": "bg-purple-50 text-purple-600",
    Internship: "bg-amber-50 text-amber-600",
    Contract: "bg-pink-50 text-pink-600",
    Remote: "bg-teal-50 text-teal-600",
  };
  return (
    <span
      className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${colors[type] || "bg-gray-50 text-gray-500"}`}
    >
      {type}
    </span>
  );
};

const CareerHomeSection = () => {
  const scrollRef = useRef(null);
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [applyCareer, setApplyCareer] = useState(null);
  const navigate = useNavigate();

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({
      left: dir === "left" ? -320 : 320,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const fetchCareers = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/career/get-all-active`);
        setCareers((res.data.data || []).slice(0, 4));
      } catch (err) {
        console.error("Failed to fetch career listings", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCareers();
  }, []);

  if (!loading && careers.length === 0) return null;

  return (
    <section className="">
      <div className="2xl:max-w-7xl 2xl:px-0 px-6 2xl:mx-auto">
        {/* ── Heading ── */}
        <div className="flex flex-col items-center text-center mb-12">
          <span className="text-primaryColor text-6xl font-serif leading-none mb-2 opacity-20 select-none">
            "
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-gray-800 dark:text-white -mt-6">
            We're <span className="text-primaryColor">Hiring</span>
          </h2>
          <p className="text-secondaryTextColor dark:text-darkModeSecondaryTextColor mt-3 max-w-xl">
            Explore open positions and become part of a team that's building
            something meaningful.
          </p>
        </div>

        {/* ── Cards Row ── */}
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-6 md:gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 hide-scrollbar"
          >
            {loading
              ? [...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="w-full sm:w-[48.5%] md:w-[20.7rem] flex-shrink-0 animate-pulse bg-white dark:bg-darkModeSecondaryBgColor rounded-2xl p-6 h-56 border border-slate-100 dark:border-0"
                >
                  <div className="h-4 bg-slate-200 dark:bg-gray-700 rounded w-1/3 mb-4" />
                  <div className="h-5 bg-slate-200 dark:bg-gray-700 rounded w-2/3 mb-3" />
                  <div className="h-3 bg-slate-200 dark:bg-gray-700 rounded w-1/2 mb-2" />
                  <div className="h-3 bg-slate-200 dark:bg-gray-700 rounded w-1/3" />
                </div>
              ))
              : careers.map((career) => (
                <div
                  key={career._id}
                  className="w-full sm:w-[48.5%] md:w-[20.7rem] flex-shrink-0 bg-white dark:bg-darkModeSecondaryBgColor rounded-2xl p-6 border border-slate-100 dark:border-0
                      shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between snap-start"
                >
                  <div>
                    <JobTypeBadge type={career.jobType} />

                    <h3 className="text-base font-bold text-gray-800 dark:text-white mt-3 line-clamp-2">
                      {career.title}
                    </h3>

                    {career.department && (
                      <p className="text-xs text-gray-400 mt-1">{career.department}</p>
                    )}

                    <div className="flex flex-col gap-1.5 mt-4 text-xs text-secondaryTextColor dark:text-darkModeSecondaryTextColor">
                      {career.location && (
                        <span className="inline-flex items-center gap-1.5">
                          <HiOutlineLocationMarker size={14} className="text-primaryColor" />
                          {career.location}
                        </span>
                      )}
                      {career.experienceLevel && (
                        <span className="inline-flex items-center gap-1.5">
                          <MdOutlineWorkOutline size={14} className="text-primaryColor" />
                          {career.experienceLevel}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2 mt-5">
                    <button
                      onClick={() => setSelectedCareer(career)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border-2 border-primaryColor text-primaryColor text-xs font-bold hover:bg-primaryColor hover:text-white transition"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => setApplyCareer(career)}
                      className="flex-1 px-3 py-2 rounded-full bg-primaryColor text-white text-xs font-bold hover:opacity-90 transition"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* ── Controls ── */}
        <div className="flex items-center justify-center gap-3 mt-8">
          <button
            onClick={() => scroll("left")}
            className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor transition"
          >
            <FiChevronLeft />
          </button>

          <button
            onClick={() => navigate("/career")}
            className="flex items-center gap-2 px-4 py-2 rounded-full border-2 border-primaryColor text-primaryColor text-sm font-semibold hover:bg-primaryColor hover:text-white transition-all duration-200 active:scale-95"
          >
            View All Openings
          </button>

          <button
            onClick={() => scroll("right")}
            className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor transition"
          >
            <FiChevronRight />
          </button>
        </div>
      </div>

      {/* ── Modals ── */}
      <CareerDetailModal
        career={selectedCareer}
        onClose={() => setSelectedCareer(null)}
        onApply={(c) => setApplyCareer(c)}
      />
      <CareerApplyModal career={applyCareer} onClose={() => setApplyCareer(null)} />
    </section>
  );
};

export default CareerHomeSection;